import React from 'react';
import { observer } from 'mobx-react-lite';
import { CollapsibleOutline, CardViewX, ButtonMenu } from '@ann/web-components';
import { CardViewXContainer } from '../../MyWorkspace.styled';
import { formatLastUpdated } from '../../../../../../../utils/dateUtils';
import { LoadMoreSection } from '../../../../components/LoadMoreSection';
import { useNavigate } from 'react-router-dom';
import {FolderCardWrapper} from '../../MyWorkspace.styled'
import {useDrivesStores} from "../../../../hook/use-drives.hook";
import { useFolderHierarchy } from '../../../../hooks/useFolderHierarchy';
import dayjs from 'dayjs';


const FoldersList = observer(({
    folders,
    selectionStore,
    onShare,
    onDuplicate,
    onRename,
    onMove,
    onFolderInfo,
    hasMore,
    isLoading,
    onLoadMore,
    disabledActions = { share: false, duplicate: false, move: false, folderInfo: false },
    hiddenActions = { share: false, duplicate: false, move: false, folderInfo: false }
}) => {
    const { activeFolderStore } = useDrivesStores();
    const navigate = useNavigate();
    const { vaultUuid, folderHierarchy: uuidHierarchy, isDriveContext } = useFolderHierarchy();

    const onOpenFolder = (e, folder) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Prevent self-reference: don't navigate if folder is already in hierarchy
        if (uuidHierarchy.includes(folder.folder_uuid)) {
            return;
        }
        
        // Build URL with complete hierarchy
        // If we're in a drive context, maintain the d/ prefix
        if (isDriveContext && vaultUuid) {
            // Drive context: /v2/drives/d/vaultUuid/folder1/folder2...
            const newHierarchy = [...uuidHierarchy, folder.folder_uuid];
            const hierarchyPath = newHierarchy.length > 0 ? newHierarchy.join('/') : '';
            const url = hierarchyPath 
                ? `/v2/drives/d/${vaultUuid}/${hierarchyPath}`
                : `/v2/drives/d/${vaultUuid}/${folder.folder_uuid}`;
            
            navigate(url, {
                state: { folderName: folder.name }
            });
        } else {
            // Backward compatibility: no drive context (My Workspace, etc.)
            const newHierarchy = [...uuidHierarchy, folder.folder_uuid];
            const hierarchyPath = newHierarchy.join('/');
            
            navigate(`/v2/drives/${hierarchyPath}`, {
                state: { folderName: folder.name }
            });
        }
    }

    return (
        <CollapsibleOutline
            itemCount={folders.length}
            title="Folders"
            defaultOpen={true}
        >
            <CollapsibleOutline.Content>
                <CardViewXContainer>
                    {folders.map((folder) => (
                        <FolderCardWrapper 
                            key={folder.folder_uuid}
                            onDoubleClick={(e)=> onOpenFolder(e, folder)}
                        >
                            <CardViewX 
                                checked={folder.isSelected}
                                isCheckboxable={folder.isHovered || folder.isSelected}
                                onCheckboxChange={() => {
                                    folder.setSelected(!folder.isSelected);
                                    selectionStore.toggleFolderSelection(folder.folder_uuid);
                                }}
                                onMouseEnter={() => folder.setHover(true)}
                                onMouseLeave={() => folder.setHover(false)}
                                showHover
                                size="medium"
                            >
                            <CardViewX.Header
                                icon="folder"
                                maxCharacters={(folder.isHovered || folder.isSelected) ? 18 : 21}
                                title={folder.name}
                                menu={
                                    <ButtonMenu
                                        customIcon="dotsMenu"
                                        disableEffects={true}
                                        menuPosition="bottom"
                                        size="medium"
                                        type="tertiary"
                                        width="180px"
                                        className="dots-menu"
                                    >
                                        <ButtonMenu.Menu icon={true}>
                                            {/* <ButtonMenu.Item
                                                label="Download"
                                                value="Download"
                                                icon="download"
                                            >
                                                Download
                                            </ButtonMenu.Item> */}
                                            <ButtonMenu.Item
                                                label="Rename"
                                                value="Rename"
                                                icon="edit"
                                                onClick={() => onRename(folder)}
                                            >
                                                Rename
                                            </ButtonMenu.Item>
                                            {/* {!hiddenActions.share && (
                                                <ButtonMenu.Item
                                                    label="Share"
                                                    value="Share"
                                                    icon="personAdd"
                                                    onClick={() => onShare(folder, 'folder')}
                                                    disabled={disabledActions.share}
                                                >
                                                    Share
                                                </ButtonMenu.Item>
                                            )}
                                            {!hiddenActions.duplicate && (
                                                <ButtonMenu.Item
                                                    label="Duplicate"
                                                    value="Duplicate"
                                                    icon="controlPointDuplicate"
                                                    onClick={() => onDuplicate(folder, 'folder')}
                                                    disabled={disabledActions.duplicate}
                                                >
                                                    Duplicate
                                                </ButtonMenu.Item>
                                            )}
                                            {!hiddenActions.move && (
                                                <ButtonMenu.Item
                                                    label="Move"
                                                    value="Move"
                                                    icon="driveFileMove"
                                                    onClick={() => onMove(folder.folder_uuid)}
                                                    disabled={disabledActions.move}
                                                >
                                                    Move
                                                </ButtonMenu.Item>
                                            )} */}
                                            {!hiddenActions.folderInfo && (
                                                <ButtonMenu.Item
                                                    label="Folder Information"
                                                    value="Folder Information"
                                                    icon="info"
                                                    onClick={() => onFolderInfo && onFolderInfo(folder)}
                                                    disabled={disabledActions.folderInfo}
                                                >
                                                    Folder Information
                                                </ButtonMenu.Item>
                                            )}
                                        </ButtonMenu.Menu>
                                    </ButtonMenu>
                                }
                            />
                                <CardViewX.Meta>
                                    <CardViewX.MetaItem
                                        icon="lastUpdated"
                                        label={folder.updated_at || folder.updatedAt 
                                            ? formatLastUpdated(folder.updated_at || folder.updatedAt).label 
                                            : 'Unknown'}
                                        tooltipContent={folder.updated_at || folder.updatedAt 
                                            ? dayjs(folder.updated_at || folder.updatedAt).format('MMM D, YYYY')
                                            : 'No update date available'}
                                    />
                                    <CardViewX.MetaItem
                                        icon="filePresent"
                                        label={`${folder.sharedCountWith || 0}`}
                                        tooltipContent={`Shared with ${folder.sharedCountWith || 0} ${(folder.sharedCountWith || 0) === 1 ? 'person' : 'people'}`}
                                    />
                                </CardViewX.Meta>
                            </CardViewX>
                        </FolderCardWrapper>
                    ))}
                    <LoadMoreSection
                        hasMore={hasMore}
                        isLoading={isLoading}
                        onLoadMore={onLoadMore}
                        skeletonType="folder"
                    />
                </CardViewXContainer>
            </CollapsibleOutline.Content>
        </CollapsibleOutline>
    );
});

export default FoldersList;
